import { Record, List } from "immutable";
import { AnyAction } from "redux";

import { defaultValues } from "@config/defaultValues";
import { IS_BROWSER } from "@config/consts";

import { PROMO_CODE_NOTIFICATION, PAYMENT_METHODS } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import {
  ACTION_SUCCESS,
  ACTION_FAIL,
  ACTION_PENDING,
} from "@services/AsyncActionCreatorFactory";
import { getQueryValue } from "@services/QueryParamsParser";

import {
  FETCH_AVAILABILITY,
  FETCH_CUSTOM_AVAILABILITY,
  FETCH_PRICING,
  FETCH_WARRANTY_PRICE_AND_CURRENCY,
  FETCH_VOUCHER,
  MAKE_RESERVATION,
  SET_VOUCHER,
  SET_SQUARE_METERS,
  SET_SHOW_OFFER,
  SET_SKIP_AGENT_ASSIGNMENT,
  SET_EXECUTION_DATE,
  SET_REQUEST_DIFFERENT_STARTING_HOUR,
  SET_TASK,
  SET_WARRANTY,
  SET_SERVICE_LOCATION,
  FETCH_PAYMENT_METHODS,
  SET_PAYMENT_METHOD,
  SET_SPECIAL_INSTRUCTIONS,
  SET_REGISTRATION_FORM,
  SET_LOCATION_UUID,
  SET_FROM_LOCAL_STORAGE,
  CANCEL_RESERVATION,
  REGISTER_CLIENT,
  RESET_VOUCHER,
  SET_ALL_TASKS_SELECTED,
  SET_DEFAULT_SELECTED_TASKS,
  ADD_LOCATION,
  SET_QUERY_PARAMS,
  LEAVE_PROCESS,
  SET_WEEK_START_DATE_V2,
} from "./endOfTenancy.actions";
import {
  EndOfTenancyAvailability,
  EndOfTenancyReducer,
  EndOfTenancyPricing,
  EndOfTenancyRegistrationForm,
  WarrantyPriceAndCurrency,
  EndOfTenancyAvailabilityV2,
} from "./endOfTenancy.typings";
import {
  endOfTenancyAvailabilityFactory,
  endOfTenancyAvailabilityInitialState,
  endOfTenancyTasks as tasks,
  endOfTenancyTasksAllSelected,
  pricingInitialState,
  registerFormInitialState,
  pricingFactory,
  warrantyPriceAndCurrencyFactory,
  endOfTenancyAvailabilityV2InitialState,
  endOfTenancyCustomAvailabilityFactory,
} from "./endOfTenancy.factories";

const getServiceLocationIdFromUrl = (url: string) =>
  !isNaN(Number(getQueryValue(url, "serviceLocationId")))
    ? Number(getQueryValue(url, "serviceLocationId"))
    : defaultValues.booking.serviceLocationId;

export const initialState = Record<EndOfTenancyReducer>({
  serviceLocationId: IS_BROWSER
    ? getServiceLocationIdFromUrl(window.location.href)
    : defaultValues.endOfTenancy.serviceLocationId,
  availablePaymentMethods: List(),
  reservationUuid: defaultValues.endOfTenancy.reservationUuid,
  calendar: Record<EndOfTenancyAvailability>(
    endOfTenancyAvailabilityInitialState,
  )(),
  calendarV2: Record<EndOfTenancyAvailabilityV2>(
    endOfTenancyAvailabilityV2InitialState,
  )(),
  availabilityLoadingState: LOADING_STATE.LOADING,
  executionDate: defaultValues.endOfTenancy.executionDate,
  form: Record<EndOfTenancyRegistrationForm>(registerFormInitialState)(),
  locationUuid: defaultValues.endOfTenancy.locationUuid,
  pricing: Record<EndOfTenancyPricing>(pricingInitialState)(),
  pricingLoadingState: LOADING_STATE.IDLE,
  isPricingFetched: defaultValues.endOfTenancy.isPricingFetched,
  requestDifferentStartingHour:
    defaultValues.endOfTenancy.requestDifferentStartingHour,
  selectedPaymentMethod: defaultValues.endOfTenancy.selectedPaymentMethod,
  mainLocationUuid: defaultValues.endOfTenancy.mainLocationUuid,
  showOffer: defaultValues.endOfTenancy.showOffer,
  specialInstructions: defaultValues.endOfTenancy.specialInstructions,
  squareMeters: defaultValues.endOfTenancy.squareMeters,
  skipAgentAssignment: defaultValues.endOfTenancy.skipAgentAssignment,
  tasks,
  warranty: defaultValues.endOfTenancy.warranty,
  warrantyPriceAndCurrency: defaultValues.endOfTenancy
    .warrantyPriceAndCurrency as WarrantyPriceAndCurrency,
  voucherToken: defaultValues.endOfTenancy.voucherToken,
  voucherMessage: defaultValues.endOfTenancy.voucherMessage,
  voucherStatus: defaultValues.endOfTenancy.voucherStatus,
  queryParams: defaultValues.endOfTenancy.queryParams,
});

const endOfTenancyReducer = (
  store: Record<EndOfTenancyReducer> = initialState(),
  action: AnyAction,
): Record<EndOfTenancyReducer> => {
  const { payload, type, params } = action;

  switch (type) {
    case SET_FROM_LOCAL_STORAGE: {
      return action.payload || store;
    }
    case `${FETCH_WARRANTY_PRICE_AND_CURRENCY}${ACTION_SUCCESS}`: {
      return store.set(
        "warrantyPriceAndCurrency",
        warrantyPriceAndCurrencyFactory(payload.data),
      );
    }
    case `${FETCH_PRICING}${ACTION_PENDING}`: {
      return store.set("pricingLoadingState", LOADING_STATE.LOADING);
    }
    case `${FETCH_PRICING}${ACTION_FAIL}`: {
      return store.set("pricingLoadingState", LOADING_STATE.FAILED);
    }
    case `${FETCH_PRICING}${ACTION_SUCCESS}`: {
      return store
        .set("pricing", pricingFactory(payload.data))
        .set("pricingLoadingState", LOADING_STATE.SUCCEEDED)
        .set("isPricingFetched", true);
    }
    case `${FETCH_AVAILABILITY}${ACTION_PENDING}`:
    case `${FETCH_CUSTOM_AVAILABILITY}${ACTION_PENDING}`: {
      return store.set("availabilityLoadingState", LOADING_STATE.LOADING);
    }
    case `${FETCH_AVAILABILITY}${ACTION_FAIL}`:
    case `${FETCH_CUSTOM_AVAILABILITY}${ACTION_FAIL}`: {
      return store.set("availabilityLoadingState", LOADING_STATE.FAILED);
    }
    case `${FETCH_AVAILABILITY}${ACTION_SUCCESS}`: {
      return store
        .set("calendar", endOfTenancyAvailabilityFactory(payload.data))
        .set("availabilityLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${FETCH_CUSTOM_AVAILABILITY}${ACTION_SUCCESS}`: {
      const isFirstRequest = !store.get("calendarV2").get("staticDays");

      return store
        .set(
          "calendarV2",
          endOfTenancyCustomAvailabilityFactory(
            payload.data,
            store.get("calendarV2"),
            params.chunkSize,
            params.isExtraRequest,
          ),
        )
        .set(
          "executionDate",
          isFirstRequest
            ? payload.data.defaultSelection
            : store.get("executionDate"),
        )
        .set("availabilityLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${FETCH_VOUCHER}${ACTION_SUCCESS}`: {
      return store
        .set("voucherToken", params.token)
        .set("voucherMessage", payload.data.translatedSuccessMessage)
        .set("voucherStatus", PROMO_CODE_NOTIFICATION.SUCCESS);
    }
    case `${FETCH_VOUCHER}${ACTION_FAIL}`: {
      return store
        .set("voucherToken", defaultValues.endOfTenancy.voucherToken)
        .set("voucherMessage", action.translatedErrorMessage)
        .set("voucherStatus", PROMO_CODE_NOTIFICATION.FAILURE);
    }
    case RESET_VOUCHER: {
      return store
        .set("voucherToken", defaultValues.endOfTenancy.voucherToken)
        .set("voucherMessage", defaultValues.endOfTenancy.voucherMessage)
        .set("voucherStatus", defaultValues.endOfTenancy.voucherStatus);
    }
    case `${FETCH_PAYMENT_METHODS}${ACTION_SUCCESS}`: {
      return store.set(
        "availablePaymentMethods",
        List<PAYMENT_METHODS>(payload.data),
      );
    }
    case SET_VOUCHER: {
      return store
        .set("voucherMessage", payload.voucherMessage)
        .set("voucherStatus", payload.voucherStatus);
    }
    case SET_PAYMENT_METHOD: {
      return store.set("selectedPaymentMethod", payload);
    }
    case SET_TASK: {
      return store.mergeIn(["tasks", payload.service], {
        ...payload,
      });
    }
    case SET_ALL_TASKS_SELECTED: {
      return store.set("tasks", endOfTenancyTasksAllSelected);
    }
    case SET_DEFAULT_SELECTED_TASKS: {
      return store.set("tasks", tasks);
    }
    case SET_SQUARE_METERS: {
      return store.set("squareMeters", payload);
    }
    case SET_SHOW_OFFER: {
      return store.set("showOffer", payload);
    }
    case SET_SKIP_AGENT_ASSIGNMENT: {
      return store.set("skipAgentAssignment", payload);
    }
    case SET_EXECUTION_DATE: {
      return store.set("executionDate", payload);
    }
    case SET_SPECIAL_INSTRUCTIONS: {
      return store.set("specialInstructions", payload);
    }
    case SET_WARRANTY: {
      return store.set("warranty", payload);
    }
    case SET_REQUEST_DIFFERENT_STARTING_HOUR: {
      return store.set("requestDifferentStartingHour", payload);
    }
    case SET_SERVICE_LOCATION: {
      return store.set("serviceLocationId", payload);
    }
    case `${ADD_LOCATION}${ACTION_SUCCESS}`: {
      return store.set("locationUuid", payload.data.uuid);
    }
    case SET_LOCATION_UUID: {
      return store.set("locationUuid", payload);
    }
    case SET_REGISTRATION_FORM: {
      return store.mergeIn(["form"], payload);
    }
    case CANCEL_RESERVATION: {
      return store.set(
        "reservationUuid",
        defaultValues.endOfTenancy.reservationUuid,
      );
    }
    case `${MAKE_RESERVATION}${ACTION_SUCCESS}`: {
      return store.set("reservationUuid", payload.data.reservationUuid);
    }
    case `${REGISTER_CLIENT}${ACTION_SUCCESS}`: {
      return store
        .set("locationUuid", payload.data.mainLocationUuid)
        .set("mainLocationUuid", payload.data.mainLocationUuid);
    }
    case SET_QUERY_PARAMS: {
      return store.set("queryParams", payload);
    }
    case LEAVE_PROCESS: {
      return initialState();
    }
    case SET_WEEK_START_DATE_V2: {
      return store.setIn(["calendarV2", "weekStartDate"], payload);
    }
    default: {
      return store;
    }
  }
};

export { endOfTenancyReducer };
